import { WritableSignal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { LastRead, PageChangedEvent, SortedLastReadValue } from '../interfaces/IPaginatedResponse';
import { IPaginationProps } from '../interfaces/IPaginationProps';
import { IAccountsAggregationProps, IAccountsPaginationProps } from '../interfaces/accounts/IAccountsPaginationProps';
import { IOtherCostsAggregationProps, IOtherCostsPaginationProps } from '../interfaces/other-costs/IOtherCostsPaginationProps';
import { IEmployeePaginationProps } from '../interfaces/employee/IEmployeePaginationProps';
import { IApprovalsPaginationProps } from '../interfaces/IApprovalsPaginationProps';

export class PaginationOperations {
  /**
   * Updates the last read signal for a specific page number.
   * This is very important for the pagination to keep track of the last read value for each page.
   * Otherwise, the pagination previous and next page will not work
   * This method has to be used always when receiving an IPaginatedResponse from the backend.
   *
   * @param {WritableSignal<LastRead>} lastReadSignal - The signal representing the last read state.
   * @param {number} newLastRead - The new last read value to be set.
   * @param {number} pageNumber - The page number for which the last read value is being updated.
   * @param {Map<number, LastRead>} pageLastReadMap - A map storing the last read values for each page.
   */
  public static updateLastReadSignal(
    lastReadSignal: WritableSignal<LastRead>,
    newLastRead: number,
    pageNumber: number,
    pageLastReadMap: Map<number, LastRead>,
  ): void {
    if (!pageLastReadMap.get(pageNumber)) {
      const { nextPageLastRead } = lastReadSignal();
      if (nextPageLastRead === 0) {
        lastReadSignal.set({ currentPageLastRead: 0, nextPageLastRead: newLastRead });
      } else {
        lastReadSignal.set({ currentPageLastRead: nextPageLastRead, nextPageLastRead: newLastRead });
      }
      pageLastReadMap.set(pageNumber, {
        currentPageLastRead: lastReadSignal().currentPageLastRead,
        nextPageLastRead: lastReadSignal().nextPageLastRead,
      });
    }
  }

  /**
   * Updates the sorted last read value signal for a specific page number.
   * This is very important for the pagination to keep track of the last read value and the sort last read value for each page, considering the sorting.
   * Otherwise, the pagination previous and next page will not work correctly.
   * This method has to be used always when receiving an IPaginatedResponse from the backend and if we have sortedLastReadValue.
   * @param {WritableSignal<SortedLastReadValue>} sortedLastReadSignal - The signal representing the sorted last read state.
   * @param {string} newSortedLastReadValue - The new sorted last read value to be set.
   * @param {number} pageNumber - The page number for which the sorted last read value is being updated.
   * @param {Map<number, SortedLastReadValue>} sortLastReadMap - A map storing the sorted last read values for each page.
   */
  public static updateSortedLastReadValue(
    sortedLastReadSignal: WritableSignal<SortedLastReadValue>,
    newSortedLastReadValue: string,
    pageNumber: number,
    sortLastReadMap: Map<number, SortedLastReadValue>,
  ): void {
    if (!sortLastReadMap.get(pageNumber)) {
      const { nextSortedLastReadValue } = sortedLastReadSignal();
      if (nextSortedLastReadValue === null) {
        sortedLastReadSignal.set({ currentSortedLastReadValue: null, nextSortedLastReadValue: newSortedLastReadValue });
      } else {
        sortedLastReadSignal.set({ currentSortedLastReadValue: nextSortedLastReadValue, nextSortedLastReadValue: newSortedLastReadValue });
      }
      sortLastReadMap.set(pageNumber, {
        currentSortedLastReadValue: sortedLastReadSignal().currentSortedLastReadValue,
        nextSortedLastReadValue: sortedLastReadSignal().nextSortedLastReadValue,
      });
    }
  }

  /**
   * Resets the pagination state.
   * This method clears the last read signal, current page signal, and maps storing the last read values.
   *
   * @param {WritableSignal<LastRead>} lastReadSignal - The signal representing the last read state.
   * @param {WritableSignal<number>} currentPageSignal - The signal representing the current page number.
   * @param {Map<number, LastRead>} pageLastReadMap - A map storing the last read values for each page.
   * @param {Map<number, SortedLastReadValue>} sortLastReadMap - A map storing the sorted last read values for each page.
   */
  public static resetPagination(
    lastReadSignal: WritableSignal<LastRead>,
    currentPageSignal: WritableSignal<number>,
    pageLastReadMap: Map<number, LastRead>,
    sortLastReadMap: Map<number, SortedLastReadValue>,
  ): void {
    lastReadSignal.set({ currentPageLastRead: 0, nextPageLastRead: 0 });
    currentPageSignal.set(1);
    pageLastReadMap.clear();
    sortLastReadMap.clear();
  }

  /**
   * Generates the query parameters for a paginated response based on the provided pagination properties.
   *
   * @template T - The type of the pagination properties.
   * @param {T} paginationProps - The pagination properties used to generate the query parameters.
   * @returns {HttpParams} The generated query parameters.
   */
  public static getPaginatedResponseQueryParameters<T extends IPaginationProps>(paginationProps: T): HttpParams {
    let params = new HttpParams({
      fromObject: {
        pageSize: paginationProps.pageSize.toString(),
        lastRead: paginationProps.lastRead?.toString(),
        filters: JSON.stringify(
          paginationProps.filters.map(filter => ({ ...filter, values: filter.values.map(value => value.toString()) })),
        ),
      },
    });

    if (paginationProps?.sort) {
      params = params.set('sort', paginationProps.sort);
    }

    if (paginationProps?.sortedLastReadValue || paginationProps?.sortedLastReadValue === '') {
      params = params.set('sortedLastReadValue', paginationProps.sortedLastReadValue);
    }

    if (paginationProps?.searchValue) {
      params = params
        .set('searchValue', paginationProps.searchValue)
        .set('comparisonOperator', paginationProps.comparisonOperator)
        .set('searchFields', paginationProps.searchFields.join(','));
    }

    return params;
  }

  /**
   * Generates the query parameters for a paginated response specific to accounts based on the provided account pagination properties.
   *
   * @param {IAccountsPaginationProps} accountPaginationProps - The pagination properties specific to accounts used to generate the query parameters.
   * @returns {HttpParams} The generated query parameters.
   */
  public static getAccountsPaginatedResponseQueryParameters(accountPaginationProps: IAccountsPaginationProps): HttpParams {
    let params: HttpParams = this.getPaginatedResponseQueryParameters<IAccountsPaginationProps>(accountPaginationProps);

    if (accountPaginationProps?.showAll) {
      params = params.set('showAll', accountPaginationProps.showAll);
    }

    if (accountPaginationProps?.showInvalid) {
      params = params.set('showInvalid', accountPaginationProps.showInvalid);
    }

    if (accountPaginationProps?.previewOnly) {
      params = params.set('previewOnly', accountPaginationProps.previewOnly);
    }

    params = params.set('includeCountryResponsible', true);

    return params;
  }

  /**
   * Generates the query parameters for a paginated response specific to other-costs based on the provided other-costs pagination properties.
   *
   * @param {IOtherCostsPaginationProps} otherCostsPaginationProps - The pagination properties specific to other-costs used to generate the query parameters.
   * @returns {HttpParams} The generated query parameters.
   */
  public static getOtherCostsPaginatedResponseQueryParameters(otherCostsPaginationProps: IOtherCostsPaginationProps): HttpParams {
    let params: HttpParams = this.getPaginatedResponseQueryParameters<IOtherCostsPaginationProps>(otherCostsPaginationProps);

    if (otherCostsPaginationProps?.showAll) {
      params = params.set('showAll', otherCostsPaginationProps.showAll);
    }

    if (otherCostsPaginationProps?.showAllAccountingMonths) {
      params = params.set('showAllAccountingMonths', otherCostsPaginationProps.showAllAccountingMonths);
    }

    return params;
  }

  public static getOtherCostsAggregationsQueryParameters(otherCostsAggregationProps: IOtherCostsAggregationProps): HttpParams {
    let params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(otherCostsAggregationProps.filters),
      },
    });

    if (otherCostsAggregationProps?.searchValue) {
      params = params
        .set('searchValue', otherCostsAggregationProps.searchValue)
        .set('comparisonOperator', otherCostsAggregationProps.comparisonOperator)
        .set('searchFields', otherCostsAggregationProps.searchFields.join(','));
    }

    if (otherCostsAggregationProps?.showAll) {
      params = params.set('showAll', otherCostsAggregationProps.showAll);
    }

    if (otherCostsAggregationProps?.previewOnly) {
      params = params.set('previewOnly', otherCostsAggregationProps.previewOnly);
    }

    if (otherCostsAggregationProps?.showAllAccountingMonths) {
      params = params.set('showAllAccountingMonths', otherCostsAggregationProps.showAllAccountingMonths);
    }

    return params;
  }

  public static getAccountAggregationsQueryParameters(accountAggregationProps: IAccountsAggregationProps): HttpParams {
    let params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(accountAggregationProps.filters),
      },
    });

    if (accountAggregationProps?.searchValue) {
      params = params
        .set('searchValue', accountAggregationProps.searchValue)
        .set('comparisonOperator', accountAggregationProps.comparisonOperator)
        .set('searchFields', accountAggregationProps.searchFields.join(','));
    }

    if (accountAggregationProps?.showAll) {
      params = params.set('showAll', accountAggregationProps.showAll);
    }

    if (accountAggregationProps?.previewOnly) {
      params = params.set('previewOnly', accountAggregationProps.previewOnly);
    }

    if (accountAggregationProps?.showInvalid) {
      params = params.set('showInvalid', accountAggregationProps.showInvalid);
    }

    return params;
  }

  /**
   * Generates the query parameters for a paginated response specific to employees based on the provided employee pagination properties.
   *
   * @param {IEmployeePaginationProps} employeePaginationProps - The pagination properties specific to employees used to generate the query parameters.
   * @returns {HttpParams} The generated query parameters.
   */
  public static getEmployeePaginatedResponseQueryParameters(employeePaginationProps: IEmployeePaginationProps): HttpParams {
    let params: HttpParams = this.getPaginatedResponseQueryParameters<IEmployeePaginationProps>(employeePaginationProps);
    if (employeePaginationProps?.onlyActive !== null) {
      params = params.set('onlyActive', employeePaginationProps.onlyActive);
    }
    if (employeePaginationProps?.isForPicker !== null) {
      params = params.set('isForPicker', employeePaginationProps.isForPicker);
    }

    return params;
  }

  /**
   * Handles the page change event and updates the pagination state accordingly.
   * It uses the correct last read value for the new page based on the last read values stored in the map.
   * It works in combination with the methods for updating the signal values and the maps storing the last read values.
   *
   * @param {WritableSignal<number>} pageSize - The signal representing the page size.
   * @param {WritableSignal<number>} currentPage - The signal representing the current page number.
   * @param {WritableSignal<LastRead>} lastRead - The signal representing the last read state.
   * @param {WritableSignal<SortedLastReadValue>} sortLastReadValue - The signal representing the sorted last read state.
   * @param {PageChangedEvent} pageChangedEvent - The event containing the details of the page change.
   * @param {Map<number, LastRead>} pageLastReadMap - A map storing the last read values for each page.
   * @param {Map<number, SortedLastReadValue>} sortLastReadMap - A map storing the sorted last read values for each page.
   * @returns {{ newLastRead: number; newSortedLastReadValue: string }} An object containing the new last read and sorted last read values.
   */
  public static onPageChanged(
    pageSize: WritableSignal<number>,
    currentPage: WritableSignal<number>,
    lastRead: WritableSignal<LastRead>,
    sortLastReadValue: WritableSignal<SortedLastReadValue>,
    pageChangedEvent: PageChangedEvent,
    pageLastReadMap: Map<number, LastRead>,
    sortLastReadMap: Map<number, SortedLastReadValue>,
  ): { newLastRead: number; newSortedLastReadValue: string } {
    if (pageChangedEvent.size !== pageSize()) {
      lastRead.set({ currentPageLastRead: 0, nextPageLastRead: 0 });
      sortLastReadValue.set({ currentSortedLastReadValue: null, nextSortedLastReadValue: null });
      pageLastReadMap.clear();
      sortLastReadMap.clear();
    }
    pageSize.set(pageChangedEvent.size);
    const newLastRead = this.getNewLastRead(pageLastReadMap, pageChangedEvent, lastRead);
    const newSortedLastReadValue = this.getNewSortedLastReadValue(sortLastReadMap, pageChangedEvent, sortLastReadValue);
    currentPage.set(pageChangedEvent.page);
    return { newLastRead, newSortedLastReadValue };
  }

  /**
   * Retrieves the new last read value for the current page based on the page change event.
   * It either uses the last read value stored in the map for previous values for the page or the next page last read value from the signal if the page number doesn't exist in the map.
   *
   * @param {Map<number, LastRead>} pageLastReadMap - A map storing the last read values for each page.
   * @param {PageChangedEvent} pageChangedEvent - The event containing the details of the page change.
   * @param {WritableSignal<LastRead>} lastRead - The signal representing the last read state.
   * @returns {number} The new last read value for the current page.
   */
  private static getNewLastRead(
    pageLastReadMap: Map<number, LastRead>,
    pageChangedEvent: PageChangedEvent,
    lastRead: WritableSignal<LastRead>,
  ) {
    const pageLastReadItemInMap = pageLastReadMap.get(pageChangedEvent.page);
    if (pageLastReadItemInMap) {
      return pageLastReadItemInMap.currentPageLastRead;
    } else {
      return lastRead().nextPageLastRead;
    }
  }

  /**
   * Retrieves the new sorted last read value for the current page based on the page change event.
   * It either uses the sorted last read value stored in the map for previous values for the page or the next sorted last read value from the signal if the page number doesn't exist in the map.
   *
   * @param {Map<number, SortedLastReadValue>} sortLastReadMap - A map storing the sorted last read values for each page.
   * @param {PageChangedEvent} pageChangedEvent - The event containing the details of the page change.
   * @param {WritableSignal<SortedLastReadValue>} sortLastReadValue - The signal representing the sorted last read state.
   * @returns {string} The new sorted last read value for the current page.
   */
  private static getNewSortedLastReadValue(
    sortLastReadMap: Map<number, SortedLastReadValue>,
    pageChangedEvent: PageChangedEvent,
    sortLastReadValue: WritableSignal<SortedLastReadValue>,
  ) {
    const sortLastReadItemInMap = sortLastReadMap.get(pageChangedEvent.page);
    if (sortLastReadItemInMap) {
      return sortLastReadItemInMap.currentSortedLastReadValue;
    } else {
      return sortLastReadValue().nextSortedLastReadValue;
    }
  }

  public static getApprovalsPaginatedResponseQueryParameters(props: IApprovalsPaginationProps): HttpParams {
    let params = new HttpParams({
      fromObject: {
        pageSize: props.pageSize.toString(),
        lastRead: props.lastRead?.toString(),
        filters: JSON.stringify(props.filters),
        month: props.month.toString(),
        year: props.year.toString(),
      },
    });

    if (props?.sort) {
      params = params.set('sort', props.sort);
    }

    if (props?.sortedLastReadValue || props?.sortedLastReadValue === '') {
      params = params.set('sortedLastReadValue', props.sortedLastReadValue);
    }

    if (props?.searchValue) {
      params = params
        .set('searchValue', props.searchValue)
        .set('comparisonOperator', props.comparisonOperator)
        .set('searchFields', props.searchFields.join(','));
    }

    return params;
  }
}
